/**
 * Hybrid Encryption Utility for External Events
 *
 * This module implements hybrid encryption using AES-256-GCM (for payload) +
 * RSA-OAEP (for key exchange) to encrypt analytics events before sending to
 * external events API.
 *
 * Why Hybrid Encryption?
 * - RSA-OAEP with 2048-bit key has a ~190-byte payload limit
 * - Typical event payloads are ~435 bytes (exceeds RSA limit)
 * - Solution: Use AES for large data, RSA to secure the AES key
 *
 * Encryption Flow:
 * 1. Generate random AES-256 key (32 bytes)
 * 2. Encrypt event payload with AES-GCM → encryptedPayload
 * 3. Encrypt AES key with RSA-OAEP → encryptedKey
 * 4. Return both (backend decrypts AES key with RSA private key, then payload with AES key)
 */

import { env } from '@/env';

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const AES_KEY_LENGTH = 256; // AES-256
const AES_IV_LENGTH = 12; // 12 bytes (96 bits) for GCM mode
const RSA_ALGORITHM = 'RSA-OAEP';
const AES_ALGORITHM = 'AES-GCM';
const HASH_ALGORITHM = 'SHA-256';
const MAX_PAYLOAD_SIZE = 10240; // 10KB warning threshold

// -----------------------------------------------------------------------------
// RSA Public Key Import
// -----------------------------------------------------------------------------

/**
 * Convert PEM-formatted public key to ArrayBuffer
 *
 * PEM format:
 * -----BEGIN PUBLIC KEY-----
 * Base64EncodedKey
 * -----END PUBLIC KEY-----
 *
 * @param pem - PEM-formatted RSA public key string
 * @returns ArrayBuffer containing the key data (compatible with Node.js and browser Web Crypto API)
 * @throws Error if PEM format is invalid
 * @internal - Exported for testing purposes
 */
export function pemToArrayBuffer(pem: string): ArrayBuffer {
  // Remove PEM headers/footers and whitespace
  const pemHeader = '-----BEGIN PUBLIC KEY-----';
  const pemFooter = '-----END PUBLIC KEY-----';

  let pemContents = pem.trim();

  if (pemContents.includes(pemHeader)) {
    pemContents = pemContents.replace(pemHeader, '');
  }

  if (pemContents.includes(pemFooter)) {
    pemContents = pemContents.replace(pemFooter, '');
  }

  // Remove all whitespace/newlines
  pemContents = pemContents.replace(/\s+/g, '');

  // Base64 decode to binary string
  const binaryString = atob(pemContents);

  // Convert binary string to ArrayBuffer
  // In Node.js, use Buffer for better Web Crypto API compatibility
  if (typeof Buffer !== 'undefined') {
    // Node.js environment - use Buffer and copy to a clean ArrayBuffer
    const buffer = Buffer.from(binaryString, 'binary');
    // Copy to a new ArrayBuffer to avoid offset issues
    return buffer.buffer.slice(buffer.byteOffset, buffer.byteOffset + buffer.byteLength);
  }

  // Browser environment - use Uint8Array fallback
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes.buffer;
}

/**
 * Import RSA public key from PEM format
 *
 * This key will be used to encrypt the AES key for secure transmission.
 * The key is imported once and cached by the service.
 *
 * @param pemKey - PEM-formatted RSA public key string
 * @returns Promise resolving to CryptoKey for RSA-OAEP encryption
 * @throws Error if key import fails (invalid format, unsupported browser, etc.)
 */
export async function importRSAPublicKey(pemKey: string): Promise<CryptoKey> {
  try {
    // Convert PEM to ArrayBuffer
    const keyData = pemToArrayBuffer(pemKey);

    // Import key using Web Crypto API
    const publicKey = await crypto.subtle.importKey(
      'spki', // SubjectPublicKeyInfo format
      keyData,
      {
        name: RSA_ALGORITHM,
        hash: HASH_ALGORITHM,
      },
      false, // not extractable
      ['encrypt'], // only for encryption
    );

    return publicKey;
  } catch (error) {
    console.error('[EncryptedEventsEncryption] Failed to import RSA public key:', error);
    throw new Error(`RSA key import failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// -----------------------------------------------------------------------------
// AES Key Generation
// -----------------------------------------------------------------------------

/**
 * Generate a random AES-256 key
 *
 * This key is generated fresh for each event encryption to ensure
 * forward secrecy and prevent key reuse.
 *
 * @returns Promise resolving to CryptoKey for AES-GCM encryption
 * @throws Error if key generation fails
 */
export async function generateAESKey(): Promise<CryptoKey> {
  try {
    const aesKey = await crypto.subtle.generateKey(
      {
        name: AES_ALGORITHM,
        length: AES_KEY_LENGTH,
      },
      true, // extractable (we need to encrypt it with RSA)
      ['encrypt'], // only for encryption
    );

    return aesKey;
  } catch (error) {
    console.error('[EncryptedEventsEncryption] Failed to generate AES key:', error);
    throw new Error(`AES key generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// -----------------------------------------------------------------------------
// AES Encryption
// -----------------------------------------------------------------------------

/**
 * Encrypt data using AES-256-GCM
 *
 * Format: [IV (12 bytes)] + [Ciphertext + Auth Tag]
 * - IV is prepended to the encrypted data
 * - Auth tag is automatically appended by GCM mode
 *
 * @param data - String data to encrypt (JSON serialized event payload)
 * @param aesKey - AES-256 CryptoKey
 * @returns Promise resolving to ArrayBuffer containing IV + encrypted data + auth tag
 * @throws Error if encryption fails
 */
export async function encryptWithAES(data: string, aesKey: CryptoKey): Promise<ArrayBuffer> {
  try {
    // Generate random IV (12 bytes for GCM)
    const iv = crypto.getRandomValues(new Uint8Array(AES_IV_LENGTH));

    // Convert string to ArrayBuffer
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);

    // Encrypt using AES-GCM
    const encryptedData = await crypto.subtle.encrypt(
      {
        name: AES_ALGORITHM,
        iv: iv,
      },
      aesKey,
      dataBuffer,
    );

    // Prepend IV to encrypted data
    // Format: [IV][Ciphertext+AuthTag]
    const result = new Uint8Array(iv.length + encryptedData.byteLength);
    result.set(iv, 0);
    result.set(new Uint8Array(encryptedData), iv.length);

    return result.buffer;
  } catch (error) {
    console.error('[EncryptedEventsEncryption] AES encryption failed:', error);
    throw new Error(`AES encryption failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// -----------------------------------------------------------------------------
// RSA Encryption (for AES key)
// -----------------------------------------------------------------------------

/**
 * Encrypt the AES key using RSA-OAEP
 *
 * This allows the backend to decrypt the AES key using its RSA private key,
 * and then use that AES key to decrypt the event payload.
 *
 * @param aesKey - AES-256 CryptoKey to encrypt
 * @param rsaPublicKey - RSA public CryptoKey
 * @returns Promise resolving to ArrayBuffer containing encrypted AES key
 * @throws Error if encryption fails
 */
export async function encryptAESKeyWithRSA(aesKey: CryptoKey, rsaPublicKey: CryptoKey): Promise<ArrayBuffer> {
  try {
    // Export AES key to raw format (32 bytes)
    const aesKeyRaw = await crypto.subtle.exportKey('raw', aesKey);

    // Encrypt AES key with RSA-OAEP
    const encryptedKey = await crypto.subtle.encrypt(
      {
        name: RSA_ALGORITHM,
      },
      rsaPublicKey,
      aesKeyRaw,
    );

    return encryptedKey;
  } catch (error) {
    console.error('[EncryptedEventsEncryption] RSA encryption of AES key failed:', error);
    throw new Error(`RSA encryption failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// -----------------------------------------------------------------------------
// Main Encryption Function
// -----------------------------------------------------------------------------

/**
 * Encrypt event payload using hybrid encryption (AES-256-GCM + RSA-OAEP)
 *
 * This is the main function that orchestrates the entire encryption process:
 * 1. Serialize payload to JSON
 * 2. Check payload size (warn if >10KB)
 * 3. Generate random AES-256 key
 * 4. Encrypt payload with AES-GCM
 * 5. Encrypt AES key with RSA-OAEP
 * 6. Base64 encode both
 * 7. Return { encryptedPayload, encryptedKey }
 *
 * @param payload - Event payload object to encrypt
 * @param rsaPublicKey - RSA public CryptoKey for encrypting the AES key
 * @returns Promise resolving to object with base64-encoded encrypted payload and key
 * @throws Error if encryption fails at any step
 */
export async function encryptEventPayload<T>(
  payload: T,
  rsaPublicKey: CryptoKey,
): Promise<{ encryptedPayload: string; encryptedKey: string }> {
  try {
    // 1. Serialize payload to JSON
    const payloadJson = JSON.stringify(payload);

    // 2. Check payload size (warn if too large)
    if (payloadJson.length > MAX_PAYLOAD_SIZE) {
      console.warn(
        `[EncryptedEventsEncryption] Payload size (${payloadJson.length} bytes) exceeds ${MAX_PAYLOAD_SIZE} bytes`,
      );
      throw new Error(`Payload too large: ${payloadJson.length} bytes (max: ${MAX_PAYLOAD_SIZE})`);
    }

    // 3. Generate random AES-256 key
    const aesKey = await generateAESKey();

    // 4. Encrypt payload with AES-GCM
    const encryptedDataBuffer = await encryptWithAES(payloadJson, aesKey);

    // 5. Encrypt AES key with RSA-OAEP
    const encryptedKeyBuffer = await encryptAESKeyWithRSA(aesKey, rsaPublicKey);

    // 6. Base64 encode both
    const encryptedPayload = btoa(String.fromCharCode(...new Uint8Array(encryptedDataBuffer)));
    const encryptedKey = btoa(String.fromCharCode(...new Uint8Array(encryptedKeyBuffer)));

    return {
      encryptedPayload,
      encryptedKey,
    };
  } catch (error) {
    console.error('[EncryptedEventsEncryption] Event payload encryption failed:', error);
    throw error; // Re-throw for caller to handle
  }
}

// -----------------------------------------------------------------------------
// Environment Check
// -----------------------------------------------------------------------------

/**
 * Check if Web Crypto API is available in the current environment
 *
 * @returns true if crypto.subtle is available, false otherwise
 */
export function isCryptoAvailable(): boolean {
  return typeof crypto !== 'undefined' && typeof crypto.subtle !== 'undefined';
}

/**
 * Check if encryption is properly configured
 *
 * @returns true if public key is configured and crypto is available, false otherwise
 */
export function isEncryptionConfigured(): boolean {
  return isCryptoAvailable() && !!env.EVENTS_EXTERNAL_PUBLIC_KEY && env.EVENTS_EXTERNAL_PUBLIC_KEY.length > 0;
}
